// src/hooks/useVideoFrame.ts
"use client";

import { useEffect, useState } from "react";
import { extractVideoFrame, peekVideoFrame } from "@/lib/videoFrame";

/**
 * Returns a data: URL for a video frame, extracted in the browser.
 * - Returns null while loading or if extraction fails.
 * - Cached per-session: subsequent mounts render synchronously.
 *
 * @param videoUrl The video URL (usually `file.download_url`).
 * @param enabled  Set false to skip work entirely (e.g. compact grids).
 */
export function useVideoFrame(
    videoUrl: string | undefined,
    enabled: boolean,
): string | null {
    const [frame, setFrame] = useState<string | null>(() =>
        enabled && videoUrl ? peekVideoFrame(videoUrl) : null,
    );

    useEffect(() => {
        if (!enabled || !videoUrl) {
            setFrame(null);
            return;
        }

        const cached = peekVideoFrame(videoUrl);
        if (cached) {
            setFrame(cached);
            return;
        }

        let cancelled = false;
        extractVideoFrame(videoUrl).then((dataUrl) => {
            if (!cancelled) setFrame(dataUrl);
        });

        return () => {
            cancelled = true;
        };
    }, [videoUrl, enabled]);

    return frame;
}